/**
 *  @class
 *  @function ModalDialog
 */
if (!customElements.get('modal-dialog')) {
  class ModalDialog extends HTMLElement {
    constructor() {
      super();

      this.age_verification = this.classList.contains('age-verification-modal');
      this.id = this.getAttribute('id');
      this.delay = parseInt(this.dataset.delay, 10) * 1000;
      this.popup = this.dataset.popup;
      this.section_id = this.dataset.sectionId;
      this.disabled = this.getAttribute('disabled') != undefined;
      this.button = this.querySelector('[id^="ModalClose-"]');

      this.button.addEventListener('click', this.hide.bind(this));

      if (!this.disabled) {
        this.addEventListener('click', (event) => {
          if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
        });
      }
      if (this.delay && this.delay > 0) {
        if (!this.getCookie()) {
          setTimeout(() => {
            this.show();
            this.button.addEventListener('click', this.setCookie.bind(this));
          }, this.delay);
        }
      }
      if (this.age_verification) {
        if (!this.getCookie()) {
          this.show();
        }
        this.button.addEventListener('click', this.setCookie.bind(this));
      }
      if (Shopify.designMode) {
        this.moved = true;
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.section_id) {
            this.show();
          }
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.section_id) {
            this.hide();
          }
        });
      }
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    // A dialog removed while open (theme editor re-render) leaves no document listeners behind.
    disconnectedCallback() {
      if (this.isConnected) return;
      this._openListeners?.abort();
      if (typeof OverlayManager !== 'undefined') OverlayManager.remove(this);
    }

    show(opener) {
      this.opener = opener || document.activeElement;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      // With app.js the dialog joins the overlay stack, so one focus trap and one Escape act on the top-most overlay
      // (a dialog opened from a drawer keeps focus). Without it, the dialog keeps focus and Escape itself.
      this._openListeners?.abort();
      if (typeof OverlayManager !== 'undefined') {
        OverlayManager.push(this, () => {
          if (!this.disabled) this.hide();
        });
      } else {
        this._openListeners = new AbortController();
        const signal = this._openListeners.signal;
        if (!this.disabled) {
          document.addEventListener('keyup', (event) => {
            if (event.key === 'Escape') this.hide();
          }, { signal });
        }
        document.addEventListener('keydown', (event) => this.trapFocus(event), { signal });
        // Safari's Tab skips links, so the keydown trap can be stepped past: focus that lands outside is brought back.
        document.addEventListener('focusin', (event) => {
          if (!this.contains(event.target)) this.hold();
        }, { signal });
      }

      setTimeout(() => {
        this.querySelector('[role="dialog"]').focus({ preventScroll: true });
      }, 100);
    }

    focusable() {
      return Array.from(this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')).filter((el) => el.getClientRects().length > 0);
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || !this.hasAttribute('open')) return;
      this._backwards = event.shiftKey;
      const focusable = this.focusable();
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !this.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      } else {
        // A browser that skips some controls on Tab (Safari skips links) can step past the last one, even onto the
        // page itself (no focusin): check where focus landed.
        setTimeout(() => this.hold(), 0);
      }
    }

    // Brings focus back inside the open dialog when it has ended up outside it.
    hold() {
      if (!this.hasAttribute('open') || this.contains(document.activeElement)) return;
      const focusable = this.focusable();
      (this._backwards ? focusable[focusable.length - 1] : focusable[0])?.focus({ preventScroll: true });
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      this._openListeners?.abort();
      if (typeof OverlayManager !== 'undefined') OverlayManager.remove(this);
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      // preventScroll: giving focus back must not move the page the shopper is reading.
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
      this.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
      });
      this.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    // Storage can be unavailable (private modes, blocked site data): the popup then shows again, nothing breaks.
    getCookie() {
      try { return window.localStorage.getItem(this.id); } catch (e) { return null; }
    }
    setCookie() {
      try { window.localStorage.setItem(this.id, JSON.stringify(new Date())); } catch (e) { /* not remembered */ }
    }
  }
  customElements.define('modal-dialog', ModalDialog);
}
if (!customElements.get('modal-opener')) {
  class ModalOpener extends HTMLElement {
    constructor() {
      super();

      const button = this.querySelector('button');

      if (!button) return;
      button.addEventListener('click', () => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (modal) modal.show(button);
      });
    }
  }
  customElements.define('modal-opener', ModalOpener);
}